import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import userService from '../services/userService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    // Expanded user state to match Django user model
    const [user, setUser] = useState({
        // Basic info
        id: null,
        email: '',
        first_name: '',
        last_name: '',
        bio: '',
        profile_picture: null,

        // Contact info
        address: '',
        country: null,
        city: '',
        state: '',
        zip_code: '',
        phone_number: '',
        corporate_email: '',

        // Social media
        x_handle: '',
        facebook_profile: '',
        linkedin_profile: '',
        instagram_handle: '',
        youtube_channel: '',
        website: '',
        tiktok_profile: '',
        pinterest_profile: '',

        // Personal info
        birth_date: null,
        gender: '',
        language_preference: '',
        timezone: '',

        // Status fields
        is_verified: false,
        is_premium_member: false,

        // Referral fields
        referral_code: '',
        referred_by: null,
        referral_link: '',
        referral_count: 0,
        referral_bonus: 0,
        referral_bonus_paid: 0,
        referral_bonus_pending: 0,
        referral_bonus_total: 0,

        // Related fields
        membership_history: []
    });

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    // Email verification state
    const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
    const [verificationEmail, setVerificationEmail] = useState('');
    const [verificationLoading, setVerificationLoading] = useState(false);

    // Initialize auth state
    useEffect(() => {
        initializeAuth();
    }, []);

    const initializeAuth = async () => {
        try {
            const token = authService.getAccessToken();
            if (token) {
                const storedUser = authService.getCurrentUser();
                if (storedUser) {
                    setUser(prevUser => ({ ...prevUser, ...storedUser }));
                    setIsAuthenticated(true);

                    // Fetch fresh user data
                    await fetchUserData();
                } else {
                    // Token exists but no user data, try to fetch
                    await fetchUserData();
                }
            }
        } catch (error) {
            console.error('Auth initialization error:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const fetchUserData = async () => {
        try {
            const result = await userService.getCurrentUser();
            if (result.success) {
                setUser(prevUser => ({ ...prevUser, ...result.data }));
                setIsAuthenticated(true);
                // Update stored user data
                localStorage.setItem('user', JSON.stringify(result.data));
            } else if (result.requiresVerification) {
                setEmailVerificationRequired(true);
                setVerificationEmail(result.email);
                setIsAuthenticated(false);
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            logout();
        }
    };

    const login = async (email, password, rememberMe = false) => {
        try {
            setLoading(true);
            const result = await authService.login(email, password, rememberMe);

            if (result.requiresVerification) {
                setEmailVerificationRequired(true);
                setVerificationEmail(result.email);
                setIsAuthenticated(false);
                return {
                    success: false,
                    requiresVerification: true,
                    message: result.message
                };
            }

            if (result.user) {
                setUser(prevUser => ({ ...prevUser, ...result.user }));
                setIsAuthenticated(true);
                setEmailVerificationRequired(false);
                return { success: true, user: result.user };
            }

            throw new Error('Login failed');
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Login failed'
            };
        } finally {
            setLoading(false);
        }
    };

    const register = async (userData) => {
        try {
            setLoading(true);
            const result = await authService.register(userData);

            // After registration, user needs to verify email
            setEmailVerificationRequired(true);
            setVerificationEmail(userData.email);

            return {
                success: true,
                user: result,
                requiresVerification: true
            };
        } catch (error) {
            console.error('Registration error:', error);
            return {
                success: false,
                error: error.response?.data || error.message || 'Registration failed'
            };
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser({
                id: null,
                email: '',
                first_name: '',
                last_name: '',
                bio: '',
                profile_picture: null,
                address: '',
                country: null,
                city: '',
                state: '',
                zip_code: '',
                phone_number: '',
                corporate_email: '',
                x_handle: '',
                facebook_profile: '',
                linkedin_profile: '',
                instagram_handle: '',
                youtube_channel: '',
                website: '',
                tiktok_profile: '',
                pinterest_profile: '',
                birth_date: null,
                gender: '',
                language_preference: '',
                timezone: '',
                is_verified: false,
                is_premium_member: false,
                referral_code: '',
                referred_by: null,
                referral_link: '',
                referral_count: 0,
                referral_bonus: 0,
                referral_bonus_paid: 0,
                referral_bonus_pending: 0,
                referral_bonus_total: 0,
                membership_history: []
            });
            setIsAuthenticated(false);
            setEmailVerificationRequired(false);
            setVerificationEmail('');
        }
    };

    const updateUser = async (userData) => {
        try {
            const result = await userService.updateCurrentUser(userData);
            if (result.success) {
                setUser(prevUser => ({ ...prevUser, ...result.data }));
                return { success: true, user: result.data };
            }
            throw new Error(result.error);
        } catch (error) {
            console.error('Update user error:', error);
            return {
                success: false,
                error: error.response?.data || error.message || 'Update failed'
            };
        }
    };

    // Email verification methods
    const verifyOTP = async (email, code) => {
        try {
            setVerificationLoading(true);
            const result = await authService.verifyOTP(email, code);

            if (result.success) {
                if (result.user) {
                    setUser(prevUser => ({ ...prevUser, ...result.user }));
                }
                setIsAuthenticated(true);
                setEmailVerificationRequired(false);
                setVerificationEmail('');
                return { success: true };
            }

            return {
                success: false,
                error: result.error || 'Verification failed'
            };
        } catch (error) {
            console.error('OTP verification error:', error);
            return {
                success: false,
                error: error.response?.data?.error || error.message || 'Verification failed'
            };
        } finally {
            setVerificationLoading(false);
        }
    };

    const resendVerification = async (email) => {
        try {
            setVerificationLoading(true);
            const result = await authService.resendVerification(email);
            return result;
        } catch (error) {
            console.error('Resend verification error:', error);
            return {
                success: false,
                error: error.response?.data?.error || error.message || 'Failed to resend verification'
            };
        } finally {
            setVerificationLoading(false);
        }
    };

    const value = {
        user,
        isAuthenticated,
        loading,
        emailVerificationRequired,
        verificationEmail,
        verificationLoading,
        login,
        register,
        logout,
        updateUser,
        verifyOTP,
        resendVerification,
        fetchUserData
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}; 